import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Avatar,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from "@mui/icons-material";
import { createBrokerAccount } from "../../../Services/BrokersApi";
import { themeColors } from "../../../theme/colors";

const AddBrokerAccountDialog = ({ open, onClose, onCreated }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  // حقل اختياري لإرسال مفتاح/قيمة إضافية إن رغبت
  const [extraKey, setExtraKey] = useState("");
  const [extraValue, setExtraValue] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setName("");
    setPhone("");
    setExtraKey("");
    setExtraValue("");
    setError(null);
  };

  const handleCreate = async () => {
    setError(null);
    if (!email || !password || !name) {
      setError("الرجاء ملء الحقول الأساسية (email, password, name).");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const payload = { email, password, name, phone };
      if (extraKey && extraValue) payload[extraKey] = extraValue;

      await createBrokerAccount(payload, token);
      if (onCreated) onCreated();
      resetForm();
    } catch (err) {
      setError((err && (err.message || err.msg || JSON.stringify(err))) || "فشل إنشاء الحساب");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          background: `linear-gradient(135deg, ${themeColors.primary.main} 0%, ${themeColors.primary.dark} 100%)`,
          color: "white",
          position: "relative",
        }}
      >
        <Avatar
          sx={{
            width: 48,
            height: 48,
            bgcolor: "rgba(255,255,255,0.25)",
            backdropFilter: "blur(10px)",
          }}
        >
          <PersonAddIcon />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            إضافة حساب عميل جديد
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            أدخل بيانات العميل لإنشاء حساب جديد
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            left: 8,
            top: 8,
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(220, 53, 69, 0.15)",
            }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={2.5}>
          <TextField
            label="البريد الإلكتروني"
            placeholder="example@domain.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.primary.main,
                },
              },
            }}
          />

          <TextField
            label="كلمة المرور"
            placeholder="••••••••"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.primary.main,
                },
              },
            }}
          />

          <TextField
            label="الاسم الكامل"
            placeholder="أدخل اسم العميل"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.primary.main,
                },
              },
            }}
          />

          <TextField
            label="رقم الهاتف"
            placeholder="+966 5xxxxxxxx"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: themeColors.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: themeColors.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: themeColors.primary.main,
                },
              },
            }}
          />
        </Box>

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: `${themeColors.primary.main}08`,
            borderRadius: 2,
            border: `1px solid ${themeColors.primary.main}20`,
          }}
        >
          <Typography variant="caption" sx={{ color: themeColors.text.secondary }}>
            💡 <strong>ملاحظة:</strong> برجاء التاكد ان البريد الالكتروني ورقم الهاتف غير مقررين
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            borderColor: themeColors.border.main,
            color: themeColors.text.secondary,
            "&:hover": {
              borderColor: themeColors.text.primary,
              bgcolor: "transparent",
            },
          }}
        >
          إلغاء
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: themeColors.primary.main,
            boxShadow: `0 4px 12px ${themeColors.primary.main}40`,
            "&:hover": {
              bgcolor: themeColors.primary.dark,
              boxShadow: `0 6px 16px ${themeColors.primary.main}60`,
            },
            "&:disabled": {
              bgcolor: themeColors.text.light,
            },
          }}
        >
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              <span>جاري الإنشاء...</span>
            </Box>
          ) : (
            "إنشاء الحساب"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddBrokerAccountDialog;
